import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import { ApiError } from '../api/client';
import { importPreview, signup, type SignupInput } from '../api/endpoints/signup';
import { getDeviceId } from '../auth/deviceId';
import { useSession } from '../auth/useSession';
import { chosenPlan, initialDraft, signupInputFrom, type SignupMode, stepError, stepsFor, updateDraft, type SignupDraft, type StepId } from '../features/signup/draft';
import { getBilling } from '../api/endpoints/planPayment';
import { DoneStep } from '../features/signup/DoneStep';
import { foundLabels, importErrorMessage, isImportLink, prefillFromImport, websiteExtras } from '../features/signup/import-prefill';
import { LivePreview } from '../features/signup/LivePreview';
import type { StepProps } from '../features/signup/Question';
import { CategoryStep, ZoneStep } from '../features/signup/steps-choice';
import { HoursStep, PlaceStep, PlanStep } from '../features/signup/steps-more';
import { EmailStep, joinSpanish, NameStep, OwnerStep, PasswordStep, PhoneStep } from '../features/signup/steps-text';
import { useStepFlow } from '../features/signup/useStepFlow';
import { WizardShell } from '../features/signup/WizardShell';
import { createMutationAttempt } from '../lib/mutation-attempt';
import { planHasAnnualBilling } from '../lib/plan';
import '../styles/signup-wizard.css';
import { currentDevice } from '../lib/country';

/** The beat between tapping an answer and the next question, so the pick is seen landing. */
const CHOICE_PAUSE_MS = 260;
const LOGIN_FAILED = 'Tu negocio quedó creado. No pudimos iniciar sesión; intenta entrar de nuevo.';

type StepErrorState = { step?: StepId; message?: string; attempt: number };

/**
 * Alta propia as a conversation: one question per screen, the business card
 * assembling itself on the side, and the plan last. The request it sends is
 * the same atomic signup as before; on success it signs the person in and
 * celebrates once before the card (paid plans) or the panel (Independientes).
 */
export function RegistroPage({ mode, inviteCode }: { mode?: SignupMode; inviteCode?: string } = {}) {
  const { login } = useSession();
  const navigate = useNavigate();
  const signupAttempt = useRef(createMutationAttempt<SignupInput>()).current;
  const signupCredentials = useRef<{ email: string; password: string } | null>(null);

  // With no ?pais= the device says where the business likely is (2 Nov 2026): its clock, then its language region.
  const [draft, setDraft] = useState<SignupDraft>(() => initialDraft(window.location.search, mode, currentDevice()));
  const [stepErrorState, setStepErrorState] = useState<StepErrorState>({ attempt: 0 });
  const [signupComplete, setSignupComplete] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [finished, setFinished] = useState(false);
  const [cardRequired, setCardRequired] = useState(false);
  const [trialEndsAt, setTrialEndsAt] = useState<string | null>(null);
  // Only with Stripe on does the login carry the trial end (the moment the lock starts); that is also when a card can be saved.
  const [canSaveCard, setCanSaveCard] = useState(false);
  // Without Stripe the login carries no trial end, but the business still has its free trial:
  // the billing view reports it, read once the new session is in place. A failed read falls back to the payment step.
  const billingQuery = useQuery({
    queryKey: ['signup-billing'],
    queryFn: getBilling,
    enabled: finished && !cardRequired && !trialEndsAt && planHasAnnualBilling(chosenPlan(draft)),
    retry: 2,
  });
  const shownTrialEndsAt = trialEndsAt ?? billingQuery.data?.trialEndsAt ?? null;
  const resolvingTrial = finished && !cardRequired && !trialEndsAt && planHasAnnualBilling(chosenPlan(draft)) && billingQuery.isPending && !billingQuery.isError;

  const steps = stepsFor(draft);
  const flow = useStepFlow(steps.length);
  const step = steps[flow.index]!;

  function update(patch: Partial<SignupDraft>) {
    setDraft((current) => updateDraft(current, patch));
    if (stepErrorState.step === step) setStepErrorState(({ attempt }) => ({ attempt }));
  }

  function next() {
    const message = stepError(step, draft);
    if (message) {
      setStepErrorState(({ attempt }) => ({ step, message, attempt: attempt + 1 }));
      return;
    }
    setStepErrorState(({ attempt }) => ({ attempt }));
    if (step === 'plan') {
      void submit();
      return;
    }
    flow.next();
  }

  function choose(patch: Partial<SignupDraft>) {
    update(patch);
    const reduced = !(window.matchMedia?.('(prefers-reduced-motion: no-preference)').matches ?? false);
    if (reduced) flow.next();
    else window.setTimeout(flow.next, CHOICE_PAUSE_MS);
  }

  async function handleImport(text: string) {
    try {
      const { data } = await importPreview(isImportLink(text) ? { url: text } : { text });
      const patch = prefillFromImport(data.fields, draft.country);
      update(patch);
      const extras = websiteExtras(data.fields);
      const later = extras.length > 0 ? [`También vimos ${joinSpanish(extras)}: los agregas desde tu panel.`] : [];
      return { found: foundLabels(patch), warnings: [...data.warnings, ...later] };
    } catch (error) {
      return { found: [], warnings: [importErrorMessage(error)] };
    }
  }

  async function submit() {
    setSubmitting(true);
    let accountCreated = signupComplete;
    try {
      if (!accountCreated) {
        const request = signupAttempt.begin(inviteCode ? { ...signupInputFrom(draft), inviteCode } : signupInputFrom(draft));
        await signup(request.input, request.key);
        signupCredentials.current = { email: request.input.email, password: request.input.password };
        signupAttempt.complete();
        accountCreated = true;
        setSignupComplete(true);
      }
      const credentials = signupCredentials.current ?? { email: draft.email.trim(), password: draft.password };
      const session = await login(credentials.email, credentials.password, getDeviceId());
      setCardRequired(Boolean(session?.cardRequired));
      // Dale only takes a card with a payment: Independientes pays nothing up front.
      setCanSaveCard(Boolean(session?.trialEndsAt) && planHasAnnualBilling(chosenPlan(draft)));
      setTrialEndsAt(session?.trialEndsAt ?? null);
      setFinished(true);
    } catch (error) {
      const message = accountCreated ? LOGIN_FAILED : signupErrorMessage(error);
      setStepErrorState(({ attempt }) => ({ step: 'plan', message, attempt: attempt + 1 }));
      if (!accountCreated) signupAttempt.fail(error);
    } finally {
      setSubmitting(false);
    }
  }

  function continueAfterSignup() {
    // A paid plan is paid here, in the product; Independientes has nothing to pay.
    if (planHasAnnualBilling(chosenPlan(draft))) void navigate({ to: '/pago', search: { desde: 'registro' } });
    else void navigate({ to: '/primer-dia' });
  }

  const stepProps: StepProps = {
    draft,
    update,
    next,
    choose,
    error: stepErrorState.step === step ? stepErrorState.message : undefined,
    attempt: stepErrorState.attempt,
  };

  return (
    <WizardShell
      stepKey={finished ? 'listo' : step}
      position={flow.index}
      total={steps.length}
      direction={flow.direction}
      leaving={flow.leaving}
      onBack={flow.index > 0 ? flow.back : undefined}
      finished={finished}
      solo={draft.mode === 'independiente'}
      preview={<LivePreview draft={draft} celebrating={finished} />}
    >
      {finished ? (
        <DoneStep
          draft={{ ...draft, plan: chosenPlan(draft) }}
          cardRequired={cardRequired}
          trialEndsAt={shownTrialEndsAt}
          canSaveCard={canSaveCard}
          resolving={resolvingTrial}
          onContinue={continueAfterSignup}
          onEnterPanel={() => void navigate({ to: '/primer-dia' })}
        />
      ) : (
        renderStep(step, stepProps, {
          onImport: handleImport,
          invited: Boolean(inviteCode),
          submitLabel: submitLabel(signupComplete, signupAttempt.isUncertain(), submitting, draft.mode === 'independiente'),
          busy: submitting,
        })
      )}
    </WizardShell>
  );
}

type Extras = {
  onImport: (text: string) => Promise<{ found: string[]; warnings: string[] }>;
  invited: boolean;
  submitLabel: string;
  busy: boolean;
};

function renderStep(step: StepId, props: StepProps, extras: Extras) {
  switch (step) {
    case 'nombre':
      return <NameStep {...props} onImport={extras.onImport} invited={extras.invited} />;
    case 'tipo':
      return <CategoryStep {...props} />;
    case 'zona':
      return <ZoneStep {...props} />;
    case 'lugar':
      return <PlaceStep {...props} />;
    case 'telefono':
      return <PhoneStep {...props} />;
    case 'dueno':
      return <OwnerStep {...props} />;
    case 'correo':
      return <EmailStep {...props} />;
    case 'clave':
      return <PasswordStep {...props} />;
    case 'horario':
      return <HoursStep {...props} />;
    case 'plan':
      return <PlanStep {...props} submitLabel={extras.submitLabel} busy={extras.busy} />;
  }
}

function submitLabel(signupComplete: boolean, uncertain: boolean, submitting: boolean, solo: boolean): string {
  if (submitting) return signupComplete ? 'Entrando…' : 'Creando…';
  if (signupComplete) return 'Entrar y continuar';
  if (uncertain) return 'Reintentar registro';
  return solo ? 'Crear mi cuenta' : 'Crear mi negocio';
}

/**
 * A 409 is deliberately vague: the backend refuses a taken address with the
 * same body as any other conflict so signup cannot be used to test whether an
 * account exists, and the copy here must not undo that.
 */
function signupErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 429) return 'Demasiados intentos. Espera un momento.';
    if (error.status === 503) return 'El registro no está abierto todavía.';
    return error.message;
  }
  return 'No pudimos completar eso ahora mismo.';
}
